import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

function Topics() {
  const [topics, setTopics] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [filter, setFilter] = useState({ agent: '', category: '', status: '', q: '' });
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    fetchTopics();
    fetchStats();
    const interval = setInterval(() => {
      fetchTopics();
      fetchStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [filter]);

  const apiCall = async (url, options = {}) => {
    const token = document.cookie.match(/token=([^;]+)/)?.[1];
    const res = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Cookie: `token=${token}` } : {}),
        ...options.headers,
      },
      credentials: 'include',
    });
    return res.json();
  };

  const fetchTopics = async () => {
    try {
      let url = '/api/topics?';
      if (filter.agent) url += `agent=${filter.agent}&`;
      if (filter.category) url += `category=${filter.category}&`;
      if (filter.status) url += `status=${filter.status}&`;
      if (filter.q) url += `q=${encodeURIComponent(filter.q)}&`;
      const data = await apiCall(url);
      setTopics(data.topics || []);
    } catch (e) {
      console.error('Failed to fetch topics:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await apiCall('/api/topics/stats');
      setStats(data);
    } catch (e) {
      console.error('Failed to fetch stats:', e);
    }
  };

  const handleCreate = async (topicData) => {
    try {
      const data = await apiCall('/api/topics', {
        method: 'POST',
        body: JSON.stringify(topicData),
      });
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success('Topic created');
        setShowCreate(false);
        fetchTopics();
        fetchStats();
      }
    } catch (e) {
      toast.error('Failed to create topic');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await apiCall(`/api/topics/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });
      toast.success(`Marked as ${newStatus}`);
      fetchTopics();
      fetchStats();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'resolved': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'archived': return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
      case 'blocked': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      infrastructure: '🏗️',
      network: '🌐',
      sales: '💼',
      support: '🛟',
      content: '✍️',
      qc: '🔍',
      customer: '👤',
      security: '🔒',
      backup: '💾',
      general: '📋',
    };
    return icons[category] || '📋';
  };

  const getAgentColor = (agent) => {
    const colors = {
      Bolt: 'text-purple-600 dark:text-purple-400',
      InfraAI: 'text-blue-600 dark:text-blue-400',
      NetworkAI: 'text-cyan-600 dark:text-cyan-400',
      ContentAI: 'text-pink-600 dark:text-pink-400',
      SupportAI: 'text-green-600 dark:text-green-400',
      RevenueAI: 'text-amber-600 dark:text-amber-400',
      QCAI: 'text-orange-600 dark:text-orange-400',
      JerBearAI: 'text-indigo-600 dark:text-indigo-400',
      ICONAI: 'text-red-600 dark:text-red-400',
      Ziplock: 'text-teal-600 dark:text-teal-400',
    };
    return colors[agent] || 'text-gray-600 dark:text-gray-400';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const [agents, setAgents] = useState([]);

  // Fetch agent list from the API (only agents that have logged to this MC)
  useEffect(() => {
    fetch('/api/topics/stats')
      .then(res => res.json())
      .then(data => {
        if (data.byAgent) {
          setAgents(Object.keys(data.byAgent).sort());
        }
      })
      .catch(err => console.error('Failed to load agents:', err));
  }, []);
  const categories = ['infrastructure', 'network', 'sales', 'support', 'content', 'qc', 'customer', 'security', 'backup', 'general'];
  const statuses = ['active', 'resolved', 'archived', 'blocked'];

  if (loading && topics.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          📋 Topics
        </h1>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          + New Topic
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow text-center">
          <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{stats.total || 0}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Topics</div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg shadow text-center">
          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{stats.active || 0}</div>
          <div className="text-sm text-blue-700 dark:text-blue-300">Active</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900 p-4 rounded-lg shadow text-center">
          <div className="text-3xl font-bold text-green-600 dark:text-green-400">{stats.resolved || 0}</div>
          <div className="text-sm text-green-700 dark:text-green-300">Resolved</div>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg shadow text-center">
          <div className="text-3xl font-bold text-gray-600 dark:text-gray-400">{stats.archived || 0}</div>
          <div className="text-sm text-gray-700 dark:text-gray-300">Archived</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <input
          type="text"
          placeholder="Search topics..."
          value={filter.q}
          onChange={(e) => setFilter({ ...filter, q: e.target.value })}
          className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm w-64"
        />
        <select
          value={filter.agent}
          onChange={(e) => setFilter({ ...filter, agent: e.target.value })}
          className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
        >
          <option value="">All Agents</option>
          {agents.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select
          value={filter.category}
          onChange={(e) => setFilter({ ...filter, category: e.target.value })}
          className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
        >
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{getCategoryIcon(c)} {c}</option>)}
        </select>
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
        >
          <option value="">All Statuses</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="flex gap-6">
        {/* Topics List — full width when no topic selected, half when one is */}
        <div className={`space-y-3 min-w-0 ${selectedTopic ? 'w-1/2' : 'w-full'}`}>
          {topics.length === 0 ? (
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
              No topics found. Agents will create topics automatically after conversations.
            </div>
          ) : (
            topics.map((topic) => (
              <div
                key={topic.id}
                onClick={() => setSelectedTopic(topic.id)}
                className={`bg-white dark:bg-gray-800 rounded-lg shadow p-4 cursor-pointer hover:shadow-lg transition-shadow border-l-4 ${
                  selectedTopic === topic.id ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{getCategoryIcon(topic.category)}</span>
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{topic.title}</h3>
                    </div>
                    {topic.summary && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{topic.summary}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs">
                      <span className={`font-medium ${getAgentColor(topic.agent)}`}>{topic.agent}</span>
                      <span className={`px-2 py-0.5 rounded ${getStatusColor(topic.status)}`}>{topic.status}</span>
                      {topic.customer && <span className="text-gray-400">👤 {topic.customer}</span>}
                      <span className="text-gray-400">📅 {formatDate(topic.last_updated)}</span>
                    </div>
                    {topic.tags && topic.tags.length > 0 && (
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {topic.tags.slice(0, 5).map(tag => (
                          <span key={tag} className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Topic Detail Panel */}
        {selectedTopic && (
          <TopicDetail
            topicId={selectedTopic}
            apiCall={apiCall}
            onClose={() => setSelectedTopic(null)}
            onStatusChange={handleStatusChange}
            getStatusColor={getStatusColor}
            getCategoryIcon={getCategoryIcon}
            getAgentColor={getAgentColor}
            formatDate={formatDate}
          />
        )}
      </div>

      {/* Create Topic Modal */}
      {showCreate && (
        <CreateTopicModal
          agents={agents}
          categories={categories}
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
}

// ============================================================
// TopicDetail Component
// ============================================================
function TopicDetail({ topicId, apiCall, onClose, onStatusChange, getStatusColor, getCategoryIcon, getAgentColor, formatDate }) {
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newEntry, setNewEntry] = useState('');

  useEffect(() => {
    fetchTopic();
  }, [topicId]);

  const fetchTopic = async () => {
    try {
      const data = await apiCall(`/api/topics/${topicId}`);
      setTopic(data);
    } catch (e) {
      console.error('Failed to fetch topic:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async () => {
    if (!newEntry.trim()) return;
    try {
      await apiCall(`/api/topics/${topicId}`, {
        method: 'PUT',
        body: JSON.stringify({ entry: newEntry }),
      });
      setNewEntry('');
      fetchTopic();
      toast.success('Entry added');
    } catch (e) {
      toast.error('Failed to add entry');
    }
  };

  if (loading) {
    return (
      <div className="w-1/2 min-w-0 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }

  if (!topic) return null;

  return (
    <div className="w-1/2 min-w-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-h-[80vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xl">{getCategoryIcon(topic.category)}</span>
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">{topic.title}</h2>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
      </div>

      <div className="flex items-center gap-3 mb-4 text-sm">
        <span className={`font-medium ${getAgentColor(topic.agent)}`}>{topic.agent}</span>
        <span className={`px-2 py-1 rounded ${getStatusColor(topic.status)}`}>{topic.status}</span>
        {topic.customer && <span className="text-gray-400">👤 {topic.customer}</span>}
      </div>

      {topic.summary && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 italic">{topic.summary}</p>
      )}

      {topic.tags && topic.tags.length > 0 && (
        <div className="flex gap-1 mb-4 flex-wrap">
          {topic.tags.map(tag => (
            <span key={tag} className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
              #{tag}
            </span>
          ))}
        </div>
      )}

      <div className="text-xs text-gray-400 mb-4 flex gap-4">
        <span>Created: {formatDate(topic.created)}</span>
        <span>Updated: {formatDate(topic.last_updated)}</span>
      </div>

      {/* Body */}
      <div className="prose prose-sm dark:prose-invert max-w-none mb-6">
        <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-sans bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
          {topic.body}
        </pre>
      </div>

      {/* Add Entry */}
      <div className="border-t dark:border-gray-700 pt-4">
        <textarea
          value={newEntry}
          onChange={(e) => setNewEntry(e.target.value)}
          placeholder="Add an update..."
          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm mb-2"
          rows={3}
        />
        <button
          onClick={handleAddEntry}
          className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
        >
          Add Update
        </button>
      </div>

      {/* Status Change */}
      <div className="flex gap-2 mt-4 pt-4 border-t dark:border-gray-700">
        <button onClick={() => onStatusChange(topic.id, 'active')} className="px-3 py-1 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded">Mark Active</button>
        <button onClick={() => onStatusChange(topic.id, 'resolved')} className="px-3 py-1 text-xs bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 rounded">Mark Resolved</button>
        <button onClick={() => onStatusChange(topic.id, 'archived')} className="px-3 py-1 text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded">Archive</button>
      </div>
    </div>
  );
}

// ============================================================
// CreateTopicModal Component
// ============================================================
function CreateTopicModal({ agents, categories, onClose, onCreate }) {
  const [title, setTitle] = useState('');
  const [agent, setAgent] = useState('');
  const [category, setCategory] = useState('general');
  const [tags, setTags] = useState('');
  const [summary, setSummary] = useState('');
  const [body, setBody] = useState('');

  const handleSubmit = () => {
    if (!title.trim()) return;
    onCreate({
      title,
      agent,
      category,
      tags: tags ? tags.split(',').map(t => t.trim()) : [],
      summary,
      body,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">New Topic</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What's the topic?"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Agent</label>
              <select
                value={agent}
                onChange={(e) => setAgent(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                {agents.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tags (comma-separated)</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="onboarding, billing, vpn-setup"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Summary</label>
            <input
              type="text"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="One-line summary"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Details</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Full description of the topic..."
              rows={5}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!title.trim()}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
            >
              Create Topic
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Topics;